import React, { useEffect, useState } from "react";
import {
  Alert,
  HStack,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react";
import { t } from "i18next";
import { delay } from "@keita/lanepatrol-sdk";
import { OsaWayApi } from "../../../../../osaway/services/OsaWayApi";

const i18Prefix = "osaway:photos.processingAlert";

enum Status {
  INIT = "INIT",
  CORRUPTED = "CORRUPTED",
  PROCESSING = "PROCESSING",
  DONE = "DONE",
}

export const UploadNotFinishedAlert: React.FC<{
  osaWayId: string;
  children?: React.ReactNode;
}> = ({ osaWayId, children }) => {
  const [corrupted, setCorrupted] = useState<boolean>(false);
  const [nPhotosInQueue, setNPhotosInQueue] = useState<number>();

  const fetchNPhotosInQueue = async () => {
    return await OsaWayApi.getPhotoUploadStatus(osaWayId).then(
      ({ data }) => data.nPhotosInQueue
    );
  };

  const pollPhotoUploadStatus = async () => {
    var nPhotosInQueue = await fetchNPhotosInQueue();
    setNPhotosInQueue(nPhotosInQueue);

    if (nPhotosInQueue === 0) {
      setCorrupted(true);
      return;
    }

    while (nPhotosInQueue > 0) {
      await delay(5000);

      nPhotosInQueue = await fetchNPhotosInQueue();
      setNPhotosInQueue(nPhotosInQueue);
    }
  };

  useEffect(() => {
    pollPhotoUploadStatus();
    // eslint-disable-next-line
  }, [osaWayId]);

  const status = (() => {
    if (nPhotosInQueue === undefined) return Status.INIT;
    if (corrupted) return Status.CORRUPTED;
    if (nPhotosInQueue === 0) return Status.DONE;
    return Status.PROCESSING;
  })();

  return (
    <Alert.Root p={4} borderRadius="5px" flexShrink={0} status="warning">
      <Alert.Indicator />
      <Stack>
        {status === Status.INIT ? (
          <HStack gap={2}>
            <Spinner size="sm" />
          </HStack>
        ) : (
          <>
            <Text fontWeight="semibold">
              {t(`${i18Prefix}.${status}.status`)}
            </Text>
            <Text fontSize="sm">{t(`${i18Prefix}.${status}.description`)}</Text>

            {status === Status.PROCESSING && (
              <HStack gap={2}>
                <Spinner size="sm" />
                <Text fontSize="sm">
                  {t(`${i18Prefix}.nPhotosInQueue`, {
                    count: nPhotosInQueue!!,
                  })}
                </Text>
              </HStack>
            )}
          </>
        )}

        <div />

        {children}
      </Stack>
    </Alert.Root>
  );
};
